import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

const SIZES = { sm: 'h-3.5 w-3.5', md: 'h-5 w-5', lg: 'h-8 w-8' } as const;
type Size = keyof typeof SIZES;

/** Read-only star display. Rounds to the nearest whole star; exposes the precise
 *  value to assistive tech. */
export function StarRating({
  value,
  size = 'md',
  className,
}: {
  value: number;
  size?: Size;
  className?: string;
}) {
  const full = Math.round(value);
  return (
    <span
      className={cn('inline-flex items-center gap-0.5', className)}
      role="img"
      aria-label={`${value.toFixed(1)} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden
          className={cn(SIZES[size], i < full ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-brand-outline-variant/40')}
        />
      ))}
    </span>
  );
}

/** Interactive star input — large tap targets, hover preview, keyboard arrows. */
export function StarRatingInput({
  value,
  onChange,
  size = 'lg',
  disabled,
  testid = 'star-input',
}: {
  value: number;
  onChange: (v: number) => void;
  size?: Size;
  disabled?: boolean;
  testid?: string;
}) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(Math.min(5, (value || 0) + 1));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(Math.max(1, (value || 1) - 1));
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Your rating"
      className="inline-flex items-center gap-1"
      data-testid={testid}
      onKeyDown={onKeyDown}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          disabled={disabled}
          tabIndex={value === n || (value === 0 && n === 1) ? 0 : -1}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onFocus={() => setHover(n)}
          onBlur={() => setHover(0)}
          onClick={() => onChange(n)}
          data-testid={`${testid}-star-${n}`}
          className={cn(
            'rounded-full p-1 transition-transform duration-fast ease-bounce hover:scale-110 active:scale-90',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-50',
          )}
        >
          <Star
            aria-hidden
            className={cn(SIZES[size], n <= shown ? 'fill-amber-400 text-amber-400' : 'fill-transparent text-brand-outline-variant/40')}
          />
        </button>
      ))}
    </div>
  );
}
